// ElectricalSafetyTable.tsx
import React from 'react';

export type ComplianceStatus = 'Complied' | 'Partially Complied' | 'Not Complied' | 'Not Applicable' | null;

export interface ElectricalSafetyItem {
  id?: string;
  description: string;
  complianceStatus: ComplianceStatus;
  score: number;
  maxScore: number;
  remarks?: string | null;
}

export interface ElectricalSafetySection {
  id: string;
  name: string;
  items: ElectricalSafetyItem[];
  totalScore: number;
  totalMaxScore: number;
  percentage: number;
}

interface ElectricalSafetyTableProps {
  section: ElectricalSafetySection;
  onChange?: (sectionId: string, itemIndex: number, updatedItem: ElectricalSafetyItem) => void;
  isEditable?: boolean;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}

// Calculate score based on compliance status
const calculateScore = (complianceStatus: ComplianceStatus, maxScore: number): number => {
  if (!complianceStatus) return 0;
  switch (complianceStatus) {
    case 'Complied':
      return maxScore;
    case 'Partially Complied':
      return maxScore / 2;
    case 'Not Complied':
      return 0;
    case 'Not Applicable':
      return maxScore; // Full marks for Not Applicable
    default:
      return 0;
  }
};

export default function ElectricalSafetyTable({
  section,
  onChange,
  isEditable = true,
  isCollapsed = false,
  onToggleCollapse,
}: ElectricalSafetyTableProps) {
  const handleComplianceStatusChange = (
    itemIndex: number,
    newStatus: ComplianceStatus
  ) => {
    if (!onChange || !isEditable) return;

    const item = section.items[itemIndex];
    const newScore = calculateScore(newStatus, item.maxScore);

    onChange(section.id, itemIndex, {
      ...item,
      complianceStatus: newStatus,
      score: newScore,
    });
  };

  const handleRemarksChange = (itemIndex: number, remarks: string) => {
    if (!onChange || !isEditable) return;
    const item = section.items[itemIndex];
    onChange(section.id, itemIndex, {
      ...item,
      remarks: remarks || null,
    });
  };

  const getStatusColor = (status: ComplianceStatus): string => {
    switch (status) {
      case 'Complied':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'Partially Complied':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'Not Complied':
        return 'bg-red-100 text-red-800 border-red-300';
      case 'Not Applicable':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      default:
        return 'bg-gray-100 text-gray-600 border-gray-300';
    }
  };

  const sectionColors: Record<string, string> = {
    'ELECTRICAL SHOCK PROTECTION': 'bg-blue-600',
    'FIRE PROTECTION - ELECTRICAL ORIGIN': 'bg-pink-600',
    'LOCKOUT TAGOUT & ELECTRICAL WORK PERMIT SYSTEM': 'bg-purple-600',
    'ELECT SAFETY AWARENESS / TRAINING': 'bg-indigo-600',
    'ELECTRICAL SYSTEMS': 'bg-teal-600',
    'ELECTRICAL STATUTORY COMPLIANCE': 'bg-orange-600',
    'LIGHTNING PROTECTION': 'bg-cyan-600',
    'EMERGENCY BACKUP SYSTEMS': 'bg-amber-600',
    'PERSONAL PROTECTIVE EQUIPMENT': 'bg-rose-600',
    'ELECTRICAL PREVENTIVE MAINTENANCE': 'bg-emerald-600',
    'PREDICTIVE & PREVENTIVE MAINTENANCE TESTS': 'bg-violet-600',
  };

  const headerColor = sectionColors[section.name] || 'bg-gray-600';

  return (
    <div className="w-full mb-6 border border-gray-300 rounded-lg overflow-hidden shadow-md">
      {/* Collapsible Header */}
      <div
        className={`${headerColor} text-white px-4 py-3 font-bold text-lg cursor-pointer flex items-center justify-between hover:opacity-90 transition-opacity`}
        onClick={onToggleCollapse}
      >
        <span>{section.name}</span>
        <div className="flex items-center gap-4">
          <div className="text-sm font-normal bg-white/20 px-3 py-1 rounded">
            Score: {section.totalScore.toFixed(2)} / {section.totalMaxScore} ({section.percentage.toFixed(2)}%)
          </div>
          <svg
            className={`w-5 h-5 transition-transform ${isCollapsed ? '' : 'rotate-180'}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </div>

      {!isCollapsed && (
        <div className="overflow-x-auto">
          <table className="min-w-full border-collapse bg-white">
            <thead>
              <tr className="bg-gray-100 border-b-2 border-gray-300">
                <th className="border border-gray-300 px-4 py-3 text-left font-bold text-sm text-gray-800 w-12">
                  #
                </th>
                <th className="border border-gray-300 px-4 py-3 text-left font-bold text-sm text-gray-800 min-w-[350px]">
                  Item Description
                </th>
                <th className="border border-gray-300 px-3 py-3 text-center font-bold text-sm text-gray-800 w-36">
                  Compliance Status
                </th>
                <th className="border border-gray-300 px-3 py-3 text-center font-bold text-sm text-gray-800 w-24">
                  SCORE
                </th>
                <th className="border border-gray-300 px-3 py-3 text-center font-bold text-sm text-gray-800 w-24">
                  MAX SCORE
                </th>
                <th className="border border-gray-300 px-3 py-3 text-center font-bold text-sm text-gray-800 min-w-[180px]">
                  REMARKS
                </th>
              </tr>
            </thead>
            <tbody>
              {section.items.map((item, idx) => (
                <tr
                  key={item.id || idx}
                  className="bg-white hover:bg-gray-50 transition-colors"
                >
                  <td className="border border-gray-300 px-4 py-3 text-center text-sm text-gray-700 font-medium">
                    {idx + 1}
                  </td>
                  <td className="border border-gray-300 px-4 py-3 text-sm text-gray-700">
                    {item.description}
                  </td>
                  <td className="border border-gray-300 px-3 py-3 text-center">
                    {isEditable ? (
                      <select
                        value={item.complianceStatus || ''}
                        onChange={(e) =>
                          handleComplianceStatusChange(
                            idx,
                            e.target.value === '' ? null : (e.target.value as ComplianceStatus)
                          )
                        }
                        className={`w-full px-3 py-2 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${getStatusColor(item.complianceStatus)}`}
                      >
                        <option value="">Select Status</option>
                        <option value="Complied">Complied</option>
                        <option value="Partially Complied">Partially Complied</option>
                        <option value="Not Complied">Not Complied</option>
                        <option value="Not Applicable">Not Applicable</option>
                      </select>
                    ) : (
                      <span
                        className={`inline-block px-3 py-1 text-xs font-semibold rounded border ${getStatusColor(item.complianceStatus)}`}
                      >
                        {item.complianceStatus || 'N/A'}
                      </span>
                    )}
                  </td>
                  <td className="border border-gray-300 px-3 py-3 text-center">
                    <span className="text-sm text-gray-700 font-medium">
                      {item.score.toFixed(2)}
                    </span>
                  </td>
                  <td className="border border-gray-300 px-3 py-3 text-center text-sm text-gray-700 font-medium">
                    {item.maxScore}
                  </td>
                  <td className="border border-gray-300 px-3 py-3">
                    {(() => {
                      const requiresRemarks = item.complianceStatus === 'Partially Complied' || item.complianceStatus === 'Not Complied';
                      const isMissing = requiresRemarks && !item.remarks?.trim();
                      return isEditable ? (
                        <div>
                          <textarea
                            value={item.remarks || ''}
                            onChange={(e) => handleRemarksChange(idx, e.target.value)}
                            placeholder={requiresRemarks ? 'Remarks required *' : 'Enter remarks'}
                            rows={2}
                            className={`w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-2 focus:border-transparent resize-y ${isMissing
                              ? 'border-red-400 bg-red-50 focus:ring-red-500 placeholder-red-400'
                              : 'border-gray-300 focus:ring-blue-500'
                              }`}
                          />
                          {isMissing && (
                            <p className="text-xs text-red-500 mt-1">Remarks required</p>
                          )}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-700 whitespace-pre-wrap break-words">
                          {item.remarks || '—'}
                        </span>
                      );
                    })()}
                  </td>
                </tr>
              ))}
              {/* Summary Row */}
              <tr className="bg-blue-100 border-t-2 border-gray-400 font-bold">
                <td colSpan={3} className="border border-gray-300 px-4 py-3 text-left text-sm text-gray-800">
                  YOUR SCORE
                </td>
                <td className="border border-gray-300 px-3 py-3 text-center text-sm text-gray-800">
                  {section.totalScore.toFixed(2)}
                </td>
                <td className="border border-gray-300 px-3 py-3 text-center text-sm text-gray-800">
                  {section.totalMaxScore}
                </td>
                <td className="border border-gray-300 px-3 py-3"></td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


